import React, { useEffect, useState } from "react";
import { Button, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Question, QuestionGroup, Questionnaire } from "./models";
import { QuestionsView } from "./QuestionsView";
import { translateReactNode } from "../../../../utils/translateReactNode";

export const QuestionnaireSubtaskView = ({group, questionnaire, submitting, onChange, onCompletion} : {group: QuestionGroup, questionnaire : Questionnaire, submitting: boolean, onChange:(group: QuestionGroup) => void, onCompletion: (group: QuestionGroup) => void }) => {
  const { title, description, content, questions } = group;
  const [disabled, setDisabled] = useState(true);
  const [groupKey, setGroupKey] = useState<string|null>();
  const { t } = useTranslation(questionnaire.cohort);

  const isRequired = (question: Question) => {
    return question.condition
      ? question.condition(questionnaire) && question.required
      : question.required
  };

  const isValid = (question: Question) => {
    return question.value && question.validation
      ? question.value && question.validation(questionnaire)
      : true;
  };

  const getFollowUpQuestions = (question: Question) => {
    if (question.options && question.value) {
      const option = question.options.find((item) => item.key === question.value);
      if (option && option.followUpQuestions) {
        return option.followUpQuestions;
      }
    }

    return [];
  };

  const hasDefaultValue = (question: Question) => {
    if (question.type === 'multiple-choice-multi-select') {
      if (Array.isArray(question.value)) {
        if (Array.isArray(question.defaultValue)) {
          return question.value.every(x => (question.defaultValue as string[]).includes(x));
        }
        return question.value.length === 0;
      } else {
        return true;
      }
    }

    return question.value === question.defaultValue;
  }

  useEffect(() => {
    const requiredQuestions: Question[] = questions.filter(isRequired);
    const invalidQuestions: Question[] = questions.filter((question) => !isValid(question));

    let index = 0;
    while (index < requiredQuestions.length) {

      const followUpQuestions = getFollowUpQuestions(requiredQuestions[index]);
      requiredQuestions.push(...followUpQuestions.filter(isRequired));
      index++;
    }

    setDisabled(requiredQuestions.some((question) => hasDefaultValue(question)) || invalidQuestions.length > 0);
  }, [group, questionnaire]);


  useEffect(() => {
    setGroupKey(group.key);
  }, [group]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [groupKey]);

  const handleOnChange = (questions: Question[]) => {
    const newGroup = {...group};
    newGroup.questions = questions;
    onChange(newGroup);
  };

  const handleOnCompletion = () => {
    onCompletion(group);
  }


  const isRequiredGroup = (group: QuestionGroup) => {
    return group.condition
      ? group.condition(questionnaire)
      : true;
  };

  const requiredGroups: QuestionGroup[] = questionnaire.groups.filter(isRequiredGroup);

  return (
    <div>
      {title && <Typography variant="h4" component='h2' style={{marginBottom: '1rem'}}>{t(title)}</Typography>}
      {description && <Typography variant="body1">{t(description)}</Typography>}
      {content && translateReactNode(content, questionnaire.cohort)}

      <div className={submitting ? 'disabled' : ''}>
        <QuestionsView questions={group.questions} questionnaire={questionnaire} onChange={handleOnChange} showInCard={true} />
      </div>

      <div style={{ minHeight: '4rem', textAlign: 'center'}}>
        {submitting && <CircularProgress />}
      </div>

      <Button variant="contained"
        disabled={disabled || submitting}
        onClick={handleOnCompletion}
        style={{width: '100%'}}
      >
        {requiredGroups.indexOf(group) + 1 === requiredGroups.length ? t("Done") : t("Next")}
      </Button>
    </div>
  );
}
